/*
 * Marker colour: a hue hashed from a seed (a Member's id, a Project's key, a Group's id), so a
 * Member's avatar, a Project's tile or a Group's dot looks the same wherever it appears and tells
 * two apart at a glance. An Area's hue is chosen from the same eight rather than hashed. It carries
 * no meaning either way, so nothing may read one as a state.
 *
 * The classes are written out whole, one entry per hue, because Tailwind finds classes by reading
 * source: a class put together at runtime from a number would never be generated.
 */

/** One of the eight identity hues, `--identity-1` to `--identity-8` in `tokens.css`. */
export type Identity = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

/** Each hue by the name a picker gives it: `projects`' own names for the eight. */
export { hueNames } from "@beacon/core/projects/hues"

// FNV-1a's 32-bit offset basis and prime, in decimal.
const offsetBasis = 2166136261
const prime = 16777619

/**
 * The hue a seed — a Member's id, a Project's key, a Group's id — always gets: FNV-1a over its
 * UTF-16 code units, case folded, so `OPS` and `ops` share one.
 */
export function identityOf(seed: string): Identity {
  const folded = seed.toLowerCase()
  let hash = offsetBasis
  for (let i = 0; i < folded.length; i++) {
    hash = Math.imul(hash ^ folded.charCodeAt(i), prime) >>> 0
  }
  return ((hash % 8) + 1) as Identity
}

/**
 * Each hue's classes: `solid` fills an avatar or a Project tile under a white letter, `tint` is the
 * pale ground, letter and hairline of a tile large enough for its letter to read in the hue, and
 * `dot` fills a Group's dot.
 */
export const identity = {
  1: {
    solid: "bg-identity-1",
    tint: "bg-identity-1/15 text-identity-1 ring-identity-1/25",
    dot: "bg-identity-1",
  },
  2: {
    solid: "bg-identity-2",
    tint: "bg-identity-2/15 text-identity-2 ring-identity-2/25",
    dot: "bg-identity-2",
  },
  3: {
    solid: "bg-identity-3",
    tint: "bg-identity-3/15 text-identity-3 ring-identity-3/25",
    dot: "bg-identity-3",
  },
  4: {
    solid: "bg-identity-4",
    tint: "bg-identity-4/15 text-identity-4 ring-identity-4/25",
    dot: "bg-identity-4",
  },
  5: {
    solid: "bg-identity-5",
    tint: "bg-identity-5/15 text-identity-5 ring-identity-5/25",
    dot: "bg-identity-5",
  },
  6: {
    solid: "bg-identity-6",
    tint: "bg-identity-6/15 text-identity-6 ring-identity-6/25",
    dot: "bg-identity-6",
  },
  7: {
    solid: "bg-identity-7",
    tint: "bg-identity-7/15 text-identity-7 ring-identity-7/25",
    dot: "bg-identity-7",
  },
  8: {
    solid: "bg-identity-8",
    tint: "bg-identity-8/15 text-identity-8 ring-identity-8/25",
    dot: "bg-identity-8",
  },
} as const satisfies Record<Identity, { solid: string; tint: string; dot: string }>
